/* ===================================================================
   Rhody Reviews api/v1 -- admin / agency domain (CORBATO, 2026-06-18).

   Cross-tenant, READ-ONLY operator endpoints. Contract:
   engine/api/v1/admin.py + docs/admin_backend_spec_2026-06-18.md.

   AUTH MODEL -- load-bearing and DIFFERENT from the rest of lib/api:
   the data-plane domains send the rr_ per-tenant session token. The admin
   lane is gated by @require_admin on the SUPABASE USER JWT (not an rr_ key),
   because admin-ness is a property of the auth user, not of a tenant. So
   every call here attaches `Authorization: Bearer <supabase access_token>`
   by reading the live session from supabase-js at call time and passing it
   to request()'s per-call token override.

   READ-ONLY: there are GETs only. No approve/post/create here (Hard Rule #1:
   no operator posting). The backend has no write path on /admin/* either.
   =================================================================== */
import { request, ApiError } from './_http';
import { supabase } from '../supabase';
import type { AnalyticsSummary, MeResponse } from './core';
import type { ActivityResponse } from './analytics';
import type { NotificationsResponse } from './notifications';
import type { RequestsSummary } from './requests';
import type { ReviewsResponse } from './reviews';
import type { BusinessResponse } from './account';

/* Resolve the current Supabase user JWT (access_token). Throws an ApiError(401)
   if there is no session, so a logged-out caller surfaces as "not an admin"
   in the UI rather than a silent unauthenticated request. */
async function adminToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    throw new ApiError(401, 'no_session', 'Not signed in.');
  }
  return token;
}

async function adminGet<T>(path: string, query?: Record<string, string | number | boolean | undefined>, signal?: AbortSignal): Promise<T> {
  const token = await adminToken();
  return request<T>(path, { token, query, signal });
}

// -------------------------------------------------------------------
// Types (mirror the engine admin.py response shapes)
// -------------------------------------------------------------------
export type AdminHealth = 'good' | 'warn' | 'bad';

export interface AdminMe {
  auth_user_id: string | null;
  email: string | null;
  role: string;
  is_admin: boolean;
  /* Whether this operator ALSO owns a client (tenant) row. An admin-only
     account (no clients row) cannot mint an rr_ data token, so the tenant
     dashboard would idle forever -- PostAuthLanding routes has_tenant===false
     operators straight to /agency. Optional so an older backend (no field)
     reads as undefined and the SPA falls through to the normal tenant flow
     rather than wrongly bouncing a tenant-owning admin to /agency. */
  has_tenant?: boolean;
}

export interface TenantRollup {
  client_id: string;
  business_name: string;
  industry: string | null;
  status: string | null;
  tier: string | null;
  onboarded_date: string | null;
  total_reviews: number;
  avg_rating: number | null;
  unanswered_count: number;
  latest_review_at: string | null;
  pending_drafts: number;
  reviews_30d: number;
  health: AdminHealth;
}

export interface TenantsResponse {
  tenants: TenantRollup[];
  count: number;
}

export interface AdminReviewRow {
  id: string;
  client_id: string;
  business_name: string;
  review_id: string | null;
  platform: string | null;
  reviewer_name: string | null;
  rating: number | null;
  review_text: string | null;
  review_date: string | null;
  reply_text: string | null;
  reply_timestamp: string | null;
}

export interface AdminReviewSummary {
  review_id: string | null;
  platform: string | null;
  reviewer_name: string | null;
  rating: number | null;
  review_text: string | null;
  review_date: string | null;
}

export interface AdminQueueRow {
  draft_id: string;
  client_id: string;
  business_name: string;
  draft_response: string | null;
  status: string;
  drafted_at: string | null;
  escalated: boolean;
  review: AdminReviewSummary | null;
}

export interface TenantDetailPresence {
  twilio_number_configured: boolean;
  google_oauth_connected: boolean;
  stripe_customer_linked: boolean;
}

export interface TenantRunRow {
  id: string;
  agent_name: string | null;
  status: string | null;
  started_at: string | null;
  completed_at: string | null;
  errors: string | null;
}

export interface RequestFunnel {
  sent: number;
  delivered_or_opened: number;
  left_review: number;
}

export interface TenantDetail extends TenantRollup {
  recent_reviews: AdminReviewRow[];
  recent_runs: TenantRunRow[];
  request_funnel: RequestFunnel;
  presence: TenantDetailPresence;
}

/* GET /admin/metrics -- LOCAL SaaS business metrics (Dashboard KB Phase 2).
   Computed from the clients table ONLY (no Stripe call), so every revenue-
   shaped figure is LIST-PRICE / approximate and labeled as such. The
   stripe_verified block reports real collected MRR only when a read-only
   Stripe snapshot exists; until then {available:false, reason}. */
export interface AdminMetricsStatusCounts {
  active: number;
  trial: number;
  canceled: number;
  paused: number;
}
export interface AdminMetricsTierMix {
  tier: string;
  display: string;
  count: number;
}
export interface AdminMetricsSignupsByMonth {
  month: string;
  signups: number;
}
export interface AdminMetricsTrialToPaid {
  value_pct: number | null;
  active: number;
  canceled: number;
  basis: string;
}
export interface AdminMetricsArpu {
  value_usd: number | null;
  basis: string;
  list_price_mrr_usd: number;
  active_clients: number;
}
export interface AdminMetricsStripeVerified {
  available: boolean;
  reason?: string;
  [k: string]: unknown;
}
export interface AdminMetrics {
  basis: string;
  note: string;
  as_of: string;
  clients_total: number;
  status_counts: AdminMetricsStatusCounts;
  tier_mix: AdminMetricsTierMix[];
  active_tier_counts: Record<string, number>;
  signups_by_month: AdminMetricsSignupsByMonth[];
  trial_to_paid: AdminMetricsTrialToPaid;
  arpu: AdminMetricsArpu;
  stripe_verified: AdminMetricsStripeVerified;
}

export interface ListAdminReviewsParams {
  client_id?: string;
  platform?: string;
  needs_reply?: boolean;
  limit?: number;
}

/* OPTION A -- the full read-only client Dashboard payload for one tenant.
   Bundles the SAME blobs the signed-in client's Dashboard reads, so the real
   Dashboard component can render in read-only mode off a single admin call.
   activity is nullable (honest null when the activity read soft-fails). */
export interface AdminTenantDashboard {
  client_id: string;
  read_only: true;
  identity: MeResponse;
  business: BusinessResponse;
  analytics_summary: AnalyticsSummary;
  activity: ActivityResponse | null;
  notifications: NotificationsResponse;
  requests_summary: RequestsSummary;
}

// -------------------------------------------------------------------
// The admin method object (folded into the barrel `api` in index.ts)
// -------------------------------------------------------------------
export const adminApi = {
  getAdminMe(signal?: AbortSignal): Promise<AdminMe> {
    return adminGet<AdminMe>('/api/v1/admin/me', undefined, signal);
  },

  getAdminMetrics(signal?: AbortSignal): Promise<AdminMetrics> {
    return adminGet<AdminMetrics>('/api/v1/admin/metrics', undefined, signal);
  },

  listTenants(signal?: AbortSignal): Promise<TenantRollup[]> {
    return adminGet<TenantsResponse>('/api/v1/admin/tenants', undefined, signal).then(
      (r) => r.tenants,
    );
  },

  getTenant(clientId: string, signal?: AbortSignal): Promise<TenantDetail> {
    return adminGet<TenantDetail>(
      `/api/v1/admin/tenants/${encodeURIComponent(clientId)}`,
      undefined,
      signal,
    );
  },

  /* OPTION A: the full read-only client Dashboard payload for an arbitrary
     tenant (admin-scoped, @require_admin, audited). Powers the operator's
     "view this client's dashboard" view, which reuses the REAL Dashboard
     component in read-only mode. READ-ONLY: there is no write counterpart. */
  getTenantDashboard(clientId: string, signal?: AbortSignal): Promise<AdminTenantDashboard> {
    return adminGet<AdminTenantDashboard>(
      `/api/v1/admin/tenants/${encodeURIComponent(clientId)}/dashboard`,
      undefined,
      signal,
    );
  },

  /* OPTION A: the full reviews list for an arbitrary tenant, in the same shape
     as GET /reviews (pending AI drafts folded in), so the REAL Reviews inbox
     renders read-only. The operator can SEE drafts but the read-only UI exposes
     no action and this lane has no write endpoint to call (Hard Rule #1). */
  getTenantReviews(clientId: string, signal?: AbortSignal): Promise<ReviewsResponse> {
    return adminGet<ReviewsResponse>(
      `/api/v1/admin/tenants/${encodeURIComponent(clientId)}/reviews`,
      undefined,
      signal,
    );
  },

  listAdminReviews(
    params: ListAdminReviewsParams = {},
    signal?: AbortSignal,
  ): Promise<AdminReviewRow[]> {
    return adminGet<{ reviews: AdminReviewRow[]; count: number }>(
      '/api/v1/admin/reviews',
      {
        client_id: params.client_id,
        platform: params.platform,
        needs_reply: params.needs_reply,
        limit: params.limit,
      },
      signal,
    ).then((r) => r.reviews);
  },

  listAdminQueue(
    params: { limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<AdminQueueRow[]> {
    return adminGet<{ queue: AdminQueueRow[]; count: number }>(
      '/api/v1/admin/queue',
      { limit: params.limit },
      signal,
    ).then((r) => r.queue);
  },
};
